/**
 * The grocery item catalog's part of an answer from the `grocery_item` domain, as a food `changes`
 * (docs/local-first-food-data.md, step 2e). Every write there answers with the item's details
 * (Grocery_Item_Service::details), which name the item, what it is a kind of and its kinds, so the
 * catalog takes all three without waiting for the next snapshot.
 *
 * A catalog entry is the snapshot's shape, `{ id, name, kind_of_id, tags }`. The details give the
 * item's tags but not its generic's or its kinds', so those keep the tags the store already has.
 */

const entry = (entities, id, name, kindOfId, tags = null) => {
	const held = entities.groceryItems[id] || null;
	return {
		...(held || {}),
		id: Number(id),
		name,
		kind_of_id: kindOfId === null || kindOfId === undefined ? null : Number(kindOfId),
		tags: tags || (held && held.tags) || [],
	};
};

/**
 * `details` as changes to the catalog. Kinds go one level deep, so the item's generic is a kind of
 * nothing, and its kinds are all of them: an item the store has as one of its kinds that the answer
 * lacks is a kind of nothing now. `replacedId`, when the answer is about another item than the one
 * asked about, is the item a rename merged away, which the catalog no longer has.
 */
export const itemDetailsChanges = (entities, details, replacedId = null) => {
	const item = details && details.item;
	if(!item || item.id === null || item.id === undefined) {
		return { upsert: {}, delete: {} };
	}
	const itemId = Number(item.id);
	const kindOf = details.kind_of || null;
	const kinds = details.kinds || [];
	const kindIds = new Set(kinds.map((kind) => Number(kind.id)));
	const upsert = [entry(entities, itemId, item.name, kindOf ? kindOf.id : null, item.tags || [])];
	if(kindOf) {
		upsert.push(entry(entities, kindOf.id, kindOf.name, null));
	}
	kinds.forEach((kind) => upsert.push(entry(entities, kind.id, kind.name, itemId)));
	Object.values(entities.groceryItems)
		.filter((held) => Number(held.kind_of_id) === itemId && !kindIds.has(Number(held.id)))
		.forEach((held) => upsert.push({ ...held, kind_of_id: null }));
	const replaced = replacedId === null || replacedId === undefined || Number(replacedId) === itemId ? [] : [Number(replacedId)];
	return { upsert: { grocery_items: upsert }, delete: { grocery_items: replaced } };
};
